import { useState, useEffect, forwardRef, useImperativeHandle } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Plus, X, Save, Loader2 } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import EvidenciasManager, { EvidenciaArchivo } from "./EvidenciasManager";

interface ImpactoAtendido {
  id: number;
  descripcion: string;
}

interface ItemMeta {
  id: string;
  numero: number;
  impactos_ids: number[];
  meta_descripcion: string;
  parametro_control_descripciones: string[];
  parametro_control_valores: string[];
  valor_referencia_descripciones: string[];
  valor_referencia_valores: string[];
  cumplimiento_si_no: 'SI' | 'NO' | '';
}

interface ItemAccion {
  id: string;
  numero: number;
  accion_descripcion: string;
  periodicidad_verificacion: string;
  porcentaje_cumplimiento: string;
  porcentaje_avance_programado: string;
  porcentaje_avance_fecha: string;
  observaciones: string;
}

interface PlanManejoManagerProps {
  obligacionId: string;
  isEditing: boolean;
  onUpdate?: (data: any) => void;
  onSaveSuccess?: () => void;
}

export interface PlanManejoManagerRef {
  savePlanData: () => Promise<void>;
}

const PlanManejoManager = forwardRef<PlanManejoManagerRef, PlanManejoManagerProps>(({ obligacionId, isEditing, onUpdate, onSaveSuccess }, ref) => {
  
  useImperativeHandle(ref, () => ({
    savePlanData: async () => {
      await handleGuardarPlan();
    }
  }));
  const { toast } = useToast();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [planManejo, setPlanManejo] = useState<any>(null);

  const [impactosAtendidos, setImpactosAtendidos] = useState<ImpactoAtendido[]>([]);
  const [itemsMetas, setItemsMetas] = useState<ItemMeta[]>([]);
  const [itemsAcciones, setItemsAcciones] = useState<ItemAccion[]>([]);
  const [observacionesGenerales, setObservacionesGenerales] = useState('');
  const [evidencias, setEvidencias] = useState<EvidenciaArchivo[]>([]);

  useEffect(() => {
    cargarPlanManejo();
  }, [obligacionId]);

  const cargarPlanManejo = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('planes_manejo')
        .select('*')
        .eq('obligacion_id', obligacionId)
        .order('created_at', { ascending: false })
        .maybeSingle();

      if (error) throw error;

      if (data) {
        setPlanManejo(data);
        const dataAny = data as any;
        setImpactosAtendidos(Array.isArray(dataAny.impactos_atendidos) ? dataAny.impactos_atendidos : []);
        setItemsMetas(Array.isArray(dataAny.items_metas) ? dataAny.items_metas : []);
        setItemsAcciones(Array.isArray(dataAny.items_acciones) ? dataAny.items_acciones : []);
        setObservacionesGenerales(typeof dataAny.observaciones_generales === 'string' ? dataAny.observaciones_generales : '');
        setEvidencias(Array.isArray(dataAny.evidencias) ? dataAny.evidencias : []);
      }
    } catch (error: any) {
      console.error('Error cargando plan de manejo:', error);
      toast({
        title: 'Error',
        description: 'No se pudo cargar el plan de manejo',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleAddImpacto = () => {
    const maxId = impactosAtendidos.length > 0 
      ? Math.max(...impactosAtendidos.map(i => i.id))
      : 0;
    setImpactosAtendidos([...impactosAtendidos, { 
      id: maxId + 1, 
      descripcion: '' 
    }]);
  };

  const handleRemoveImpacto = (id: number) => {
    setImpactosAtendidos(impactosAtendidos.filter(i => i.id !== id));
  };

  const handleUpdateImpacto = (id: number, descripcion: string) => {
    setImpactosAtendidos(impactosAtendidos.map(i => 
      i.id === id ? { ...i, descripcion } : i
    ));
  };

  const handleAddItemMeta = () => {
    const nuevoItem: ItemMeta = {
      id: crypto.randomUUID(),
      numero: itemsMetas.length + 1,
      impactos_ids: [],
      meta_descripcion: '',
      parametro_control_descripciones: [''],
      parametro_control_valores: [''],
      valor_referencia_descripciones: [''],
      valor_referencia_valores: [''],
      cumplimiento_si_no: ''
    };
    setItemsMetas([...itemsMetas, nuevoItem]);
  };

  const handleRemoveItemMeta = (id: string) => {
    const nuevosItems = itemsMetas.filter(i => i.id !== id);
    setItemsMetas(nuevosItems.map((item, idx) => ({ ...item, numero: idx + 1 })));
  };

  const handleUpdateItemMeta = (id: string, campo: keyof ItemMeta, valor: any) => {
    setItemsMetas(itemsMetas.map(item => 
      item.id === id ? { ...item, [campo]: valor } : item
    ));
  };

  const handleAddCeldaParametro = (itemId: string, tipo: 'descripcion' | 'valor') => {
    setItemsMetas(itemsMetas.map(item => {
      if (item.id === itemId) {
        if (tipo === 'descripcion') {
          return { ...item, parametro_control_descripciones: [...item.parametro_control_descripciones, ''] };
        } else {
          return { ...item, parametro_control_valores: [...item.parametro_control_valores, ''] };
        }
      }
      return item;
    }));
  };

  const handleAddCeldaValorReferencia = (itemId: string, tipo: 'descripcion' | 'valor') => {
    setItemsMetas(itemsMetas.map(item => {
      if (item.id === itemId) {
        if (tipo === 'descripcion') {
          return { ...item, valor_referencia_descripciones: [...item.valor_referencia_descripciones, ''] };
        } else {
          return { ...item, valor_referencia_valores: [...item.valor_referencia_valores, ''] };
        }
      }
      return item;
    }));
  };

  const handleUpdateCeldaParametro = (itemId: string, index: number, tipo: 'descripcion' | 'valor', valor: string) => {
    setItemsMetas(itemsMetas.map(item => {
      if (item.id === itemId) {
        if (tipo === 'descripcion') {
          const nuevas = [...item.parametro_control_descripciones];
          nuevas[index] = valor;
          return { ...item, parametro_control_descripciones: nuevas };
        } else {
          const nuevos = [...item.parametro_control_valores];
          nuevos[index] = valor;
          return { ...item, parametro_control_valores: nuevos };
        }
      }
      return item;
    }));
  };

  const handleUpdateCeldaValorReferencia = (itemId: string, index: number, tipo: 'descripcion' | 'valor', valor: string) => {
    setItemsMetas(itemsMetas.map(item => {
      if (item.id === itemId) {
        if (tipo === 'descripcion') {
          const nuevas = [...item.valor_referencia_descripciones];
          nuevas[index] = valor;
          return { ...item, valor_referencia_descripciones: nuevas };
        } else {
          const nuevos = [...item.valor_referencia_valores];
          nuevos[index] = valor;
          return { ...item, valor_referencia_valores: nuevos };
        }
      }
      return item;
    }));
  };

  const handleAddItemAccion = () => {
    const nuevoItem: ItemAccion = {
      id: crypto.randomUUID(),
      numero: itemsAcciones.length + 1,
      accion_descripcion: '',
      periodicidad_verificacion: '',
      porcentaje_cumplimiento: '',
      porcentaje_avance_programado: '',
      porcentaje_avance_fecha: '',
      observaciones: ''
    };
    setItemsAcciones([...itemsAcciones, nuevoItem]);
  };

  const handleRemoveItemAccion = (id: string) => {
    const nuevosItems = itemsAcciones.filter(i => i.id !== id);
    setItemsAcciones(nuevosItems.map((item, idx) => ({ ...item, numero: idx + 1 })));
  };

  const handleUpdateItemAccion = (id: string, campo: keyof ItemAccion, valor: any) => {
    setItemsAcciones(itemsAcciones.map(item => 
      item.id === id ? { ...item, [campo]: valor } : item
    ));
  };

  const handleGuardarPlan = async () => {
    if (!user) {
      toast({
        title: 'Error',
        description: 'Debes iniciar sesión para guardar el plan de manejo',
        variant: 'destructive',
      });
      return;
    }

    try {
      setSaving(true);

      const planData = {
        obligacion_id: obligacionId,
        user_id: user.id,
        impactos_atendidos: impactosAtendidos as any,
        items_metas: itemsMetas as any,
        items_acciones: itemsAcciones as any,
        observaciones_generales: observacionesGenerales,
        evidencias: evidencias as any,
      };

      let result;
      if (planManejo?.id) {
        result = await supabase
          .from('planes_manejo')
          .update(planData as any)
          .eq('id', planManejo.id)
          .select()
          .single();
      } else {
        result = await supabase
          .from('planes_manejo')
          .insert(planData as any)
          .select()
          .single();
      }

      if (result.error) throw result.error;

      toast({
        title: 'Éxito',
        description: 'Plan de manejo guardado correctamente',
      });

      onUpdate?.(planData);
      onSaveSuccess?.();
    } catch (error: any) {
      console.error('Error guardando plan de manejo:', error);
      toast({
        title: 'Error',
        description: 'No se pudo guardar el plan de manejo',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Impactos Atendidos */}
      <Card className="border-2">
        <CardHeader className="bg-blue-50/50">
          <CardTitle className="text-base font-bold">Impactos Atendidos</CardTitle>
        </CardHeader>
        <CardContent className="pt-6">
          {isEditing ? (
            <div className="space-y-3">
              {impactosAtendidos.map((impacto, index) => (
                <div key={impacto.id} className="flex gap-2">
                  <div className="flex items-center justify-center min-w-[40px] h-10 bg-primary/10 rounded font-semibold text-sm">
                    {index + 1}
                  </div>
                  <Input
                    value={impacto.descripcion}
                    onChange={(e) => handleUpdateImpacto(impacto.id, e.target.value)}
                    placeholder="Descripción del impacto"
                    className="flex-1"
                  />
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => handleRemoveImpacto(impacto.id)}
                    className="shrink-0"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              ))}
              <Button onClick={handleAddImpacto} variant="outline" className="w-full">
                <Plus className="h-4 w-4 mr-2" />
                Agregar Impacto
              </Button>
            </div>
          ) : (
            <div className="space-y-2">
              {impactosAtendidos.length === 0 ? (
                <p className="text-muted-foreground">No hay impactos registrados</p>
              ) : (
                impactosAtendidos.map((impacto, index) => (
                  <div key={impacto.id} className="flex gap-2 items-start">
                    <span className="font-medium text-sm mt-1">{index + 1}.</span>
                    <p className="flex-1">{impacto.descripcion}</p>
                  </div>
                ))
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* SECCIÓN 1: CUMPLIMIENTO DE METAS */}
      <Card className="border-2">
        <CardHeader className="bg-green-50/50">
          <CardTitle className="text-base font-bold uppercase">
            Cumplimiento de Metas (Indicadores de Éxito)
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-6 space-y-6">
          {itemsMetas.length === 0 && !isEditing ? (
            <p className="text-muted-foreground">No hay metas registradas</p>
          ) : (
            itemsMetas.map((item) => (
              <Card key={item.id} className="border-2 border-green-200">
                <CardHeader className="pb-3 bg-green-50/30">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="flex items-center justify-center w-8 h-8 bg-green-600 text-white rounded-full font-bold text-sm">
                        {item.numero}
                      </div>
                      <h3 className="font-semibold">Ítem {item.numero}</h3>
                    </div>
                    {isEditing && (
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleRemoveItemMeta(item.id)}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </CardHeader>
                <CardContent className="space-y-4 pt-4">
                  {isEditing ? (
                    <>
                      {/* Impactos Seleccionados */}
                      <div>
                        <Label className="font-semibold">Impactos Atendidos</Label>
                        <div className="mt-2 space-y-2">
                          {impactosAtendidos.map((impacto, index) => (
                            <div key={impacto.id} className="flex items-center gap-2">
                              <Checkbox
                                checked={item.impactos_ids.includes(impacto.id)}
                                onCheckedChange={(checked) => {
                                  const nuevosIds = checked
                                    ? [...item.impactos_ids, impacto.id]
                                    : item.impactos_ids.filter(id => id !== impacto.id);
                                  handleUpdateItemMeta(item.id, 'impactos_ids', nuevosIds);
                                }}
                              />
                              <Label className="text-sm">{index + 1}. {impacto.descripcion}</Label>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* 1. Metas */}
                      <div>
                        <Label className="font-semibold">1. Metas - Descripción</Label>
                        <Textarea
                          value={item.meta_descripcion}
                          onChange={(e) => handleUpdateItemMeta(item.id, 'meta_descripcion', e.target.value)}
                          placeholder="Descripción de la meta"
                          className="mt-2"
                          rows={3}
                        />
                      </div>

                      {/* 2. Parámetro de Control Medido */}
                      <div className="grid md:grid-cols-2 gap-4">
                        <div>
                          <Label className="font-semibold">2. Parámetro de Control Medido - Descripción</Label>
                          <div className="mt-2 space-y-2">
                            {item.parametro_control_descripciones.map((desc, idx) => (
                              <Input
                                key={idx}
                                value={desc}
                                onChange={(e) => handleUpdateCeldaParametro(item.id, idx, 'descripcion', e.target.value)}
                                placeholder={`Descripción ${idx + 1}`}
                              />
                            ))}
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleAddCeldaParametro(item.id, 'descripcion')}
                              className="w-full"
                            >
                              <Plus className="h-3 w-3 mr-1" />
                              Agregar celda
                            </Button>
                          </div>
                        </div>
                        <div>
                          <Label className="font-semibold">2. Parámetro de Control Medido - Valor</Label>
                          <div className="mt-2 space-y-2">
                            {item.parametro_control_valores.map((val, idx) => (
                              <Input
                                key={idx}
                                value={val}
                                onChange={(e) => handleUpdateCeldaParametro(item.id, idx, 'valor', e.target.value)}
                                placeholder={`Valor ${idx + 1}`}
                              />
                            ))}
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleAddCeldaParametro(item.id, 'valor')}
                              className="w-full"
                            >
                              <Plus className="h-3 w-3 mr-1" />
                              Agregar celda
                            </Button>
                          </div>
                        </div>
                      </div>

                      {/* 3. Valor de Referencia */}
                      <div className="grid md:grid-cols-2 gap-4">
                        <div>
                          <Label className="font-semibold">3. Valor de Referencia - Descripción</Label>
                          <div className="mt-2 space-y-2">
                            {item.valor_referencia_descripciones.map((desc, idx) => (
                              <Input
                                key={idx}
                                value={desc}
                                onChange={(e) => handleUpdateCeldaValorReferencia(item.id, idx, 'descripcion', e.target.value)}
                                placeholder={`Descripción ${idx + 1}`}
                              />
                            ))}
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleAddCeldaValorReferencia(item.id, 'descripcion')}
                              className="w-full"
                            >
                              <Plus className="h-3 w-3 mr-1" />
                              Agregar celda
                            </Button>
                          </div>
                        </div>
                        <div>
                          <Label className="font-semibold">3. Valor de Referencia - Valor</Label>
                          <div className="mt-2 space-y-2">
                            {item.valor_referencia_valores.map((val, idx) => (
                              <Input
                                key={idx}
                                value={val}
                                onChange={(e) => handleUpdateCeldaValorReferencia(item.id, idx, 'valor', e.target.value)}
                                placeholder={`Valor ${idx + 1}`}
                              />
                            ))}
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleAddCeldaValorReferencia(item.id, 'valor')}
                              className="w-full"
                            >
                              <Plus className="h-3 w-3 mr-1" />
                              Agregar celda
                            </Button>
                          </div>
                        </div>
                      </div>

                      {/* 4. Cumplimiento */}
                      <div>
                        <Label className="font-semibold">4. Cumplimiento</Label>
                        <div className="flex gap-4 mt-2">
                          <Button
                            variant={item.cumplimiento_si_no === 'SI' ? 'default' : 'outline'}
                            onClick={() => handleUpdateItemMeta(item.id, 'cumplimiento_si_no', 'SI')}
                            className="flex-1"
                          >
                            SI
                          </Button>
                          <Button
                            variant={item.cumplimiento_si_no === 'NO' ? 'default' : 'outline'}
                            onClick={() => handleUpdateItemMeta(item.id, 'cumplimiento_si_no', 'NO')}
                            className="flex-1"
                          >
                            NO
                          </Button>
                        </div>
                      </div>
                    </>
                  ) : (
                    <>
                      {/* Vista de lectura */}
                      <div className="space-y-4">
                        {/* Impactos atendidos */}
                        {item.impactos_ids.length > 0 && (
                          <div>
                            <Label className="font-semibold">Impactos Atendidos:</Label>
                            <ul className="list-disc list-inside mt-2 space-y-1">
                              {item.impactos_ids.map(impactoId => {
                                const impacto = impactosAtendidos.find(i => i.id === impactoId);
                                return impacto ? (
                                  <li key={impactoId} className="text-sm">{impacto.descripcion}</li>
                                ) : null;
                              })}
                            </ul>
                          </div>
                        )}
                        
                        {/* Meta */}
                        {item.meta_descripcion && (
                          <div>
                            <Label className="font-semibold">1. Meta:</Label>
                            <p className="text-sm mt-1">{item.meta_descripcion}</p>
                          </div>
                        )}

                        {/* Parámetro de control */}
                        {(item.parametro_control_descripciones.length > 0 || item.parametro_control_valores.length > 0) && (
                          <div>
                            <Label className="font-semibold">2. Parámetro de Control Medido:</Label>
                            <div className="grid md:grid-cols-2 gap-4 mt-2">
                              <div>
                                <p className="text-xs font-medium text-muted-foreground mb-1">Descripción:</p>
                                {item.parametro_control_descripciones.map((desc, idx) => (
                                  <p key={idx} className="text-sm">{desc}</p>
                                ))}
                              </div>
                              <div>
                                <p className="text-xs font-medium text-muted-foreground mb-1">Valor:</p>
                                {item.parametro_control_valores.map((val, idx) => (
                                  <p key={idx} className="text-sm">{val}</p>
                                ))}
                              </div>
                            </div>
                          </div>
                        )}

                        {/* Valor de referencia */}
                        {(item.valor_referencia_descripciones.length > 0 || item.valor_referencia_valores.length > 0) && (
                          <div>
                            <Label className="font-semibold">3. Valor de Referencia:</Label>
                            <div className="grid md:grid-cols-2 gap-4 mt-2">
                              <div>
                                <p className="text-xs font-medium text-muted-foreground mb-1">Descripción:</p>
                                {item.valor_referencia_descripciones.map((desc, idx) => (
                                  <p key={idx} className="text-sm">{desc}</p>
                                ))}
                              </div>
                              <div>
                                <p className="text-xs font-medium text-muted-foreground mb-1">Valor:</p>
                                {item.valor_referencia_valores.map((val, idx) => (
                                  <p key={idx} className="text-sm">{val}</p>
                                ))}
                              </div>
                            </div>
                          </div>
                        )}

                        {/* Cumplimiento */}
                        <div>
                          <Label className="font-semibold">4. Cumplimiento:</Label>
                          <Badge className={item.cumplimiento_si_no === 'SI' ? 'bg-green-100 text-green-700 mt-2' : 'bg-red-100 text-red-700 mt-2'}>
                            {item.cumplimiento_si_no || 'No especificado'}
                          </Badge>
                        </div>
                      </div>
                    </>
                  )}
                </CardContent>
            </Card>
          ))
          )}
          
          {isEditing && (
            <Button onClick={handleAddItemMeta} variant="outline" className="w-full">
              <Plus className="h-4 w-4 mr-2" />
              Agregar Ítem de Meta
            </Button>
          )}
        </CardContent>
      </Card>

      {/* SECCIÓN 2: CUMPLIMIENTO DE ACCIONES */}
      <Card className="border-2">
        <CardHeader className="bg-amber-50/50">
          <CardTitle className="text-base font-bold uppercase">
            Cumplimiento de las Acciones del Proyecto (Indicadores de Cumplimiento)
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-6 space-y-6">
          {itemsAcciones.length === 0 && !isEditing ? (
            <p className="text-muted-foreground">No hay acciones registradas</p>
          ) : (
            itemsAcciones.map((item) => (
              <Card key={item.id} className="border-2 border-amber-200">
                <CardHeader className="pb-3 bg-amber-50/30">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="flex items-center justify-center w-8 h-8 bg-amber-600 text-white rounded-full font-bold text-sm">
                        {item.numero}
                      </div>
                      <h3 className="font-semibold">Ítem {item.numero}</h3>
                    </div>
                    {isEditing && (
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleRemoveItemAccion(item.id)}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </CardHeader>
                <CardContent className="space-y-4 pt-4">
                  {isEditing ? (
                    <>
                      {/* 5. Acciones de Manejo */}
                      <div>
                        <Label className="font-semibold">5. Acciones de Manejo, Corrección o Compensación</Label>
                        <Textarea
                          value={item.accion_descripcion}
                          onChange={(e) => handleUpdateItemAccion(item.id, 'accion_descripcion', e.target.value)}
                          placeholder="Descripción de la acción"
                          className="mt-2"
                          rows={3}
                        />
                      </div>

                      {/* 6. Acción de Verificación Periódica */}
                      <div className="grid md:grid-cols-2 gap-4">
                        <div>
                          <Label className="font-semibold">6. Periodicidad de la Verificación</Label>
                          <Input
                            value={item.periodicidad_verificacion}
                            onChange={(e) => handleUpdateItemAccion(item.id, 'periodicidad_verificacion', e.target.value)}
                            placeholder="Ej: Mensual, Trimestral..."
                            className="mt-2"
                          />
                        </div>
                        <div>
                          <Label className="font-semibold">6. % de Cumplimiento</Label>
                          <Input
                            value={item.porcentaje_cumplimiento}
                            onChange={(e) => handleUpdateItemAccion(item.id, 'porcentaje_cumplimiento', e.target.value)}
                            placeholder="Ej: 85%"
                            className="mt-2"
                          />
                        </div>
                      </div>

                      {/* 7. Acciones de Verificación */}
                      <div className="grid md:grid-cols-2 gap-4">
                        <div>
                          <Label className="font-semibold">7. % de Avance Programado</Label>
                          <Input
                            value={item.porcentaje_avance_programado}
                            onChange={(e) => handleUpdateItemAccion(item.id, 'porcentaje_avance_programado', e.target.value)}
                            placeholder="Ej: 90%"
                            className="mt-2"
                          />
                        </div>
                        <div>
                          <Label className="font-semibold">7. % de Avance a la Fecha</Label>
                          <Input
                            value={item.porcentaje_avance_fecha}
                            onChange={(e) => handleUpdateItemAccion(item.id, 'porcentaje_avance_fecha', e.target.value)}
                            placeholder="Ej: 85%"
                            className="mt-2"
                          />
                        </div>
                      </div>

                      {/* 8. Observaciones */}
                      <div>
                        <Label className="font-semibold">8. Observaciones</Label>
                        <Textarea
                          value={item.observaciones}
                          onChange={(e) => handleUpdateItemAccion(item.id, 'observaciones', e.target.value)}
                          placeholder="Observaciones específicas de este ítem"
                          className="mt-2"
                          rows={2}
                        />
                      </div>
                    </>
                  ) : (
                    <>
                      {/* Vista de lectura */}
                      <div className="space-y-4">
                        {/* Acción */}
                        {item.accion_descripcion && (
                          <div>
                            <Label className="font-semibold">5. Acción de Manejo:</Label>
                            <p className="text-sm mt-1">{item.accion_descripcion}</p>
                          </div>
                        )}

                        {/* Verificación */}
                        {(item.periodicidad_verificacion || item.porcentaje_cumplimiento) && (
                          <div>
                            <Label className="font-semibold">6. Verificación Periódica:</Label>
                            <div className="grid md:grid-cols-2 gap-4 mt-2">
                              {item.periodicidad_verificacion && (
                                <div>
                                  <p className="text-xs font-medium text-muted-foreground mb-1">Periodicidad:</p>
                                  <p className="text-sm">{item.periodicidad_verificacion}</p>
                                </div>
                              )}
                              {item.porcentaje_cumplimiento && (
                                <div>
                                  <p className="text-xs font-medium text-muted-foreground mb-1">% Cumplimiento:</p>
                                  <p className="text-sm">{item.porcentaje_cumplimiento}</p>
                                </div>
                              )}
                            </div>
                          </div>
                        )}

                        {/* Avance */}
                        {(item.porcentaje_avance_programado || item.porcentaje_avance_fecha) && (
                          <div>
                            <Label className="font-semibold">7. Avance:</Label>
                            <div className="grid md:grid-cols-2 gap-4 mt-2">
                              {item.porcentaje_avance_programado && (
                                <div>
                                  <p className="text-xs font-medium text-muted-foreground mb-1">% Programado:</p>
                                  <p className="text-sm">{item.porcentaje_avance_programado}</p>
                                </div>
                              )}
                              {item.porcentaje_avance_fecha && (
                                <div>
                                  <p className="text-xs font-medium text-muted-foreground mb-1">% a la Fecha:</p>
                                  <p className="text-sm">{item.porcentaje_avance_fecha}</p>
                                </div>
                              )}
                            </div>
                          </div>
                        )}

                        {/* Observaciones */}
                        {item.observaciones && (
                          <div>
                            <Label className="font-semibold">8. Observaciones:</Label>
                            <p className="text-sm mt-1 whitespace-pre-wrap">{item.observaciones}</p>
                          </div>
                        )}
                      </div>
                    </>
                  )}
                </CardContent>
            </Card>
          ))
          )}
          
          {isEditing && (
            <Button onClick={handleAddItemAccion} variant="outline" className="w-full">
              <Plus className="h-4 w-4 mr-2" />
              Agregar Ítem de Acción
            </Button>
          )}
        </CardContent>
      </Card>

      {/* Observaciones Generales */}
      <Card className="border-2">
        <CardHeader className="bg-purple-50/50">
          <CardTitle className="text-base font-bold">Observaciones Generales</CardTitle>
        </CardHeader>
        <CardContent className="pt-6">
          {isEditing ? (
            <Textarea
              value={observacionesGenerales}
              onChange={(e) => setObservacionesGenerales(e.target.value)}
              placeholder="Observaciones generales del plan de manejo"
              rows={5}
            />
          ) : (
            <p className="text-sm whitespace-pre-wrap">
              {observacionesGenerales || <span className="text-muted-foreground">Sin observaciones generales</span>}
            </p>
          )}
        </CardContent>
      </Card>

      {/* Evidencias */}
      {isEditing && (
        <EvidenciasManager
          evidencias={evidencias}
          onEvidenciasUpdate={setEvidencias}
          readOnly={false}
        />
      )}
    </div>
  );
});

PlanManejoManager.displayName = 'PlanManejoManager';

export default PlanManejoManager;